import { CalendarClock, GripVertical, Printer } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  UNIFORM_STAGE_VALUES,
  type ProductionOrderSummaryType,
} from '@aeris/contracts'
import { Avatar, Badge, InlineAlert, RowActions, cn } from '@aeris/ui'
import { canMoveStage, daysInStage } from '@aeris/uniforms'

import { dayText } from '../lib/production-columns.tsx'
import {
  ART_STATUS_META,
  STAGE_META,
  type UniformStage,
} from '../lib/production-stage-meta.tsx'
import { documentRef } from '../lib/sales-labels.ts'
import { useStageMove } from '../lib/use-stage-move.tsx'
import { useUniformViewer } from '../lib/use-uniform-viewer.ts'

/**
 * O quadro da produção — as etapas em colunas, um cartão por pedido (F171).
 *
 * É a segunda cara da mesma fila: os mesmos filtros, a mesma rota, a mesma
 * troca de etapa. O que muda é a pergunta que ele responde de uma olhada —
 * "onde está cada pedido, e o que está parado".
 *
 * ## Dois jeitos de mover, uma regra
 *
 * Arrastar o cartão para outra coluna é o atalho do mouse. "Mover para…" no
 * menu do cartão é o caminho que serve a todos — teclado, leitor de tela e o
 * celular, onde arrastar não existe. Os dois chamam `useStageMove`, e as
 * colunas que a pessoa não alcança (`canMoveStage`) não aceitam o cartão nem
 * aparecem no menu.
 */

type Order = ProductionOrderSummaryType

export const ProductionBoard = ({
  items,
  byStage,
  onOpen,
}: {
  items: readonly Order[]
  /** A contagem do filtro inteiro, que o servidor fez. */
  byStage: Readonly<Partial<Record<UniformStage, number>>>
  onOpen: (id: string) => void
}): ReactNode => {
  const { actor } = useUniformViewer()
  const mover = useStageMove({ manages: actor.manages })
  const [dragging, setDragging] = useState<Order | null>(null)
  const [over, setOver] = useState<UniformStage | null>(null)
  const now = new Date()

  const accepts = (stage: UniformStage): boolean =>
    dragging !== null &&
    dragging.stage !== stage &&
    canMoveStage(actor, dragging.stage, stage)

  return (
    <div className="flex flex-col gap-2">
      {mover.error === null ? null : (
        <InlineAlert tone="danger">{mover.error.message}</InlineAlert>
      )}

      {/* O quadro rola de lado dentro dele mesmo; a página não. */}
      <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
        {UNIFORM_STAGE_VALUES.map((stage) => {
          const meta = STAGE_META[stage]
          const cards = items.filter((order) => order.stage === stage)
          const count = byStage[stage] ?? cards.length
          const target = accepts(stage)

          return (
            <section
              key={stage}
              aria-label={`${meta.label}: ${String(count)} pedidos`}
              className={cn(
                'flex w-72 shrink-0 snap-start flex-col gap-2 rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-sunken) p-2',
                target && 'border-dashed border-(--border-strong)',
                over === stage && target && 'bg-(--surface-hover)'
              )}
              onDragOver={(event) => {
                if (!target) return
                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
                setOver(stage)
              }}
              onDragLeave={() => {
                setOver((current) => (current === stage ? null : current))
              }}
              onDrop={(event) => {
                event.preventDefault()
                if (dragging !== null && target) mover.move(dragging, stage)
                setDragging(null)
                setOver(null)
              }}
            >
              <header className="flex items-center justify-between gap-2 px-1">
                <h2 className="flex items-center gap-1.5 text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
                  {meta.icon}
                  {meta.label}
                </h2>
                <span className="text-(length:--text-xs) text-(--text-muted) tabular-nums">
                  {count}
                </span>
              </header>

              {cards.length === 0 ? (
                <p className="px-1 py-4 text-center text-(length:--text-xs) text-(--text-muted)">
                  Nenhum pedido
                </p>
              ) : (
                <ol className="flex flex-col gap-2">
                  {cards.map((order) => (
                    <li key={order.id}>
                      <BoardCard
                        order={order}
                        now={now}
                        reachable={UNIFORM_STAGE_VALUES.filter(
                          (to) =>
                            to !== order.stage &&
                            canMoveStage(actor, order.stage, to)
                        )}
                        moving={mover.pendingId === order.id}
                        onOpen={onOpen}
                        onMove={(to) => {
                          mover.move(order, to)
                        }}
                        onDragStart={() => {
                          setDragging(order)
                        }}
                        onDragEnd={() => {
                          setDragging(null)
                          setOver(null)
                        }}
                      />
                    </li>
                  ))}
                </ol>
              )}
            </section>
          )
        })}
      </div>

      {mover.dialogs}
    </div>
  )
}

const BoardCard = ({
  order,
  now,
  reachable,
  moving,
  onOpen,
  onMove,
  onDragStart,
  onDragEnd,
}: {
  order: Order
  now: Date
  reachable: readonly UniformStage[]
  moving: boolean
  onOpen: (id: string) => void
  onMove: (to: UniformStage) => void
  onDragStart: () => void
  onDragEnd: () => void
}): ReactNode => {
  const draggable = reachable.length > 0 && !moving
  const days = daysInStage(new Date(order.stageChangedAt), now)
  const art = ART_STATUS_META[order.artStatus]
  const name = documentRef('order', order.number)

  return (
    <article
      draggable={draggable}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', order.id)
        onDragStart()
      }}
      onDragEnd={onDragEnd}
      aria-busy={moving}
      className={cn(
        'flex flex-col gap-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface) p-2.5 shadow-(--shadow-sm)',
        draggable && 'cursor-grab active:cursor-grabbing',
        moving && 'opacity-60'
      )}
    >
      <div className="flex items-start gap-1">
        {draggable ? (
          <GripVertical
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 text-(--text-muted)"
          />
        ) : null}
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={() => {
            onOpen(order.id)
          }}
        >
          <span className="block text-(length:--text-xs) text-(--text-muted) tabular-nums">
            {name}
          </span>
          <span className="block truncate text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
            {order.customerName}
          </span>
          {order.customerCity === null ? null : (
            <span className="block truncate text-(length:--text-xs) text-(--text-secondary)">
              {order.customerCity}
            </span>
          )}
        </button>
        {reachable.length === 0 ? null : (
          <RowActions
            name={name}
            items={reachable.map((to) => ({
              label: `Mover para ${STAGE_META[to].label}`,
              onSelect: () => {
                onMove(to)
              },
              disabled: moving,
            }))}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {order.stage === 'arte' ? (
          <Badge tone={art.tone}>{art.label}</Badge>
        ) : null}
        {order.late ? <Badge tone="danger">Atrasado</Badge> : null}
        {order.batches.length > 0 ? (
          <Badge tone="info">
            {`Dividido em ${String(order.batches.length + 1)} levas`}
          </Badge>
        ) : null}
        {order.printers.length === 0 ? null : (
          <Badge
            tone="info"
            icon={<Printer aria-hidden="true" className="h-3 w-3" />}
          >
            {order.printers.map((printer) => printer.name).join(', ')}
          </Badge>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 text-(length:--text-xs) text-(--text-secondary)">
        <span className="flex items-center gap-1 tabular-nums">
          {order.dispatchDate === null ? null : (
            <span
              className={cn(
                'flex items-center gap-1',
                order.late && 'font-(--weight-medium) text-(--danger)'
              )}
            >
              <CalendarClock aria-hidden="true" className="h-3 w-3" />
              {dayText(order.dispatchDate)}
            </span>
          )}
          <span>· {order.pieces} peças</span>
        </span>
        <span className="flex items-center gap-1">
          {order.designerName === null ||
          order.designerUserId === null ? null : (
            <span title={`Designer: ${order.designerName}`}>
              <Avatar
                label={order.designerName}
                seed={order.designerUserId}
                className="h-6 w-6 text-[0.625rem]"
              />
            </span>
          )}
          {order.salespersonName === null ||
          order.salespersonUserId === null ? null : (
            <span title={`Atendente: ${order.salespersonName}`}>
              <Avatar
                label={order.salespersonName}
                seed={order.salespersonUserId}
                className="h-6 w-6 text-[0.625rem]"
              />
            </span>
          )}
        </span>
      </div>

      <p className="text-(length:--text-xs) text-(--text-muted)">
        {days === 0
          ? 'Entrou nesta etapa hoje'
          : days === 1
            ? 'Há 1 dia nesta etapa'
            : `Há ${String(days)} dias nesta etapa`}
      </p>
    </article>
  )
}
